import { inject, Injectable } from "@angular/core";

import { combineLatest, Observable, of, switchMap } from "rxjs";

import {
  HiRecordResponse,
  MonthCardData,
  ParsedHiRecord,
  PlanRecord,
  PlansResponse,
  RequestState,
  RequestStatus,
  SummaryCards,
} from "@shared/models";
import { HiRecordsService, PlanApiService } from "@shared/services";

@Injectable({
  providedIn: "root",
})
export class SummaryCardsService {
  hiRecordService = inject(HiRecordsService);
  planApiService = inject(PlanApiService);

  getSummaryCardsData(): Observable<RequestState<SummaryCards>> {
    const hi1Record$ = this.hiRecordService.getHiRecord("current", false);
    const hi2Record$ = this.hiRecordService.getHiRecord("next", false);
    const plans$ = this.planApiService.getPlan(false, 0);

    return combineLatest([hi1Record$, hi2Record$, plans$]).pipe(
      switchMap(([hi1RecordResponse, hi2RecordResponse, plansResponse]) =>
        this.handleCombinedResponse(hi1RecordResponse, hi2RecordResponse, plansResponse)
      )
    );
  }

  private handleCombinedResponse(
    hi1RecordResponse: RequestState<HiRecordResponse>,
    hi2RecordResponse: RequestState<HiRecordResponse>,
    plansResponse: RequestState<PlansResponse>
  ): Observable<RequestState<SummaryCards>> {
    if (hi1RecordResponse.isLoading || hi2RecordResponse.isLoading || plansResponse.isLoading) {
      return of(new RequestState<SummaryCards>(RequestStatus.IsLoadingNoResponse));
    }

    if (!hi1RecordResponse.response || !hi2RecordResponse.response || !plansResponse.response) {
      return of(new RequestState<SummaryCards>(RequestStatus.HasErrorNoResponse));
    }

    const parsedHi1Record = this.hiRecordService.parseHiRecord(hi1RecordResponse.response.raw, "current");
    const parsedHi2Record = this.hiRecordService.parseHiRecord(hi2RecordResponse.response.raw, "next");

    const createMonthCardData = (
      parsedRecord: ParsedHiRecord,
      plan: PlanRecord,
      isNextMonth = false
    ): MonthCardData => ({
      imax: isNextMonth && parsedRecord.prelimAward ? plan.imax : parsedRecord.imax,
      vmax: isNextMonth && parsedRecord.prelimAward ? plan.vmax : parsedRecord.vmax,
      planProj: plan.proj,
      hiProj: isNextMonth && parsedRecord.prelimAward ? 0 : parsedRecord.proj,
      payProj: isNextMonth && parsedRecord.prelimAward ? 0 : parsedRecord.pproj,
      source: parsedRecord.prelimAward
        ? { class: "status warning", icon: "", text: "Source: Plan" }
        : { class: "status success", icon: "", text: "Source: FOS" },
      raw: parsedRecord.raw,
    });

    const currentMonth = createMonthCardData(parsedHi1Record, plansResponse.response.plans[0]);
    const nextMonth = createMonthCardData(parsedHi2Record, plansResponse.response.plans[1], true);

    const summaryCards: SummaryCards = {
      currentMonth,
      nextMonth,
    };

    return of(new RequestState<SummaryCards>(RequestStatus.IsCompleteWithResponse, summaryCards));
  }
}
